import { supabase } from './supabase';

/**
 * ============================================================
 * CORE ACCOUNTING RULE — READ BEFORE TOUCHING ANY TRANSACTION
 * ============================================================
 *
 * EVERY business event must post a matching journal entry.
 * No exceptions. No silent saves.
 *
 *   Event                          | Journal must fire
 *   -------------------------------|------------------------------------------
 *   Invoice created                | DR AR / CR Revenue  (+ cost lines)
 *   Invoice voided / admin re-edit | voidJournal() ALL related JEs (AR AND cost)
 *   Cost item saved on invoice     | DR COGS / CR AP  at TOTAL amount (not per-pax × count)
 *   Payment received from client   | DR Bank / CR AR
 *   Payment made to supplier       | DR AP / CR Bank
 *   Deposit received               | DR Bank / CR Client Deposits (2010)
 *   Deposit applied to invoice     | DR Client Deposits (2010) / CR AR
 *   Package supplier added         | DR COGS / CR AP
 *
 * VOIDING RULES:
 *   - When an invoice is voided or re-edited, call voidJournal() for EVERY
 *     journal entry tagged to that invoice (both revenue JEs and cost JEs).
 *   - Never delete journal lines. Always reverse them with voidJournal().
 *   - After voiding, re-post fresh entries with the corrected amounts.
 *
 * AMOUNTS:
 *   - Cost amounts are the TOTAL supplier charge, already in LKR base.
 *   - If per_pax is true, multiply unit cost × pax count BEFORE calling postJournal.
 *   - base_debit / base_credit must equal debit_amount × exchange_rate.
 *
 * If you add a new save/edit/void flow and skip the journal, the balance
 * sheet, AP, AR and ledgers will silently be wrong. Always journal.
 * ============================================================
 */

// Highest existing number already used in this series (JE- non-reversals, or REV-
// reversals), so the next one is a real MAX+1 — NOT a row COUNT+1. Counting rows and
// assuming the series is perfectly dense broke in production: a handful of entries were
// once inserted directly with explicit, deliberately-chosen numbers (a one-off manual
// correction, checked at the time not to collide with whatever the count-based counter
// had reached) — which left the row count permanently behind the true highest number
// used. Every save after that walked the stale count forward until it collided with
// those explicit numbers, and kept colliding on every single attempt (not just an
// occasional race), because re-deriving the same COUNT+1 on retry doesn't change if
// nothing else has been inserted in between. MAX+1 has no such failure mode regardless
// of how any gap got there. A single legacy row uses an unrelated Date.now()-based
// number (from an older, different numbering scheme) — its digit length is filtered out
// so it can never poison every future number with an enormous suffix.
async function fetchMaxEntryNumber(prefix, isReversal) {
  const prefixLen = prefix.length;
  let max = 0;
  // Supabase/PostgREST caps an unbounded select at 1000 rows — with several thousand
  // journal_entries now, a single unpaginated query only ever saw the oldest 1000 (in
  // default order), computing a MAX from a tiny early slice of the series instead of the
  // real one. Page through with .range() until exhausted, same pattern already used for
  // this exact reason elsewhere (AccountLedger.js).
  const PAGE = 1000;
  let from = 0;
  while (true) {
    let q = supabase.from('journal_entries').select('entry_number');
    q = isReversal ? q.not('reversal_of', 'is', null) : q.is('reversal_of', null);
    q = q.like('entry_number', `${prefix}%`).range(from, from + PAGE - 1);
    const { data } = await q;
    for (const row of data || []) {
      const suffix = row.entry_number.slice(prefixLen);
      if (suffix.length <= 6) {
        const n = parseInt(suffix, 10);
        if (!isNaN(n) && n > max) max = n;
      }
    }
    if (!data?.length || data.length < PAGE) break;
    from += PAGE;
  }
  return max;
}

// Two staff saving invoices/vouchers at the exact same moment can both read the same
// MAX and both try to insert MAX+1, which the unique constraint on entry_number
// correctly rejects. Rather than let that raw "duplicate key" error abort the whole save
// (leaving, e.g., an invoice row already committed with no journal behind it), retry a
// few times — each attempt advances to baseMax+1+attempt regardless of whether anything
// else was inserted in between, so it always makes real progress instead of recomputing
// the same already-taken number.
async function insertEntryWithRetry(prefix, isReversal, buildPayload, maxAttempts = 6) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Re-fetch the true current max on every attempt, not just once before the loop.
    // On a busy multi-user system, more than `maxAttempts` other entries can land between
    // this call's first fetch and its retries — reusing a stale baseMax then means every
    // retry keeps colliding with numbers already taken in the meantime, exhausting all
    // attempts instead of ever converging. Re-fetching means each retry targets whatever
    // is actually next, however far the series has moved on.
    const baseMax = await fetchMaxEntryNumber(prefix, isReversal);
    const entry_number = `${prefix}${String(baseMax + 1).padStart(4, '0')}`;
    const { data, error } = await supabase.from('journal_entries').insert(buildPayload(entry_number)).select().single();
    if (!error) return data;
    const isEntryNumberCollision = error.code === '23505' && /entry_number/.test(error.message || error.details || '');
    if (!isEntryNumberCollision || attempt === maxAttempts - 1) throw error;
    await new Promise(r => setTimeout(r, 20 + Math.random() * 60));
  }
}

/**
 * Post a double-entry journal entry.
 * lines: [{ account_id, debit_amount, credit_amount, description }]
 */
export async function postJournal({ date, description, reference_type, reference_id, currency_id, exchange_rate = 1, lines, created_by, is_auto_posted = true, branch_id = null }) {
  const total_debit = lines.reduce((s, l) => s + parseFloat(l.debit_amount || 0), 0);
  const total_credit = lines.reduce((s, l) => s + parseFloat(l.credit_amount || 0), 0);

  const je = await insertEntryWithRetry('JE-', false, (entry_number) => ({
    entry_number,
    entry_date: date,
    description,
    reference_type,
    reference_id,
    is_auto_posted,
    total_debit,
    total_credit,
    currency_id,
    exchange_rate,
    status: 'POSTED',
    created_by,
    branch_id,
  }));

  const jlRows = lines.map((l, i) => ({
    journal_entry_id: je.id,
    account_id: l.account_id,
    description: l.description || description,
    debit_amount: parseFloat(l.debit_amount || 0),
    credit_amount: parseFloat(l.credit_amount || 0),
    currency_id,
    exchange_rate,
    base_debit: parseFloat(l.debit_amount || 0) * exchange_rate,
    base_credit: parseFloat(l.credit_amount || 0) * exchange_rate,
    party_type: l.party_type || null,
    party_id: l.party_id || null,
    line_order: i,
    branch_id,
  }));

  const { error: jlErr } = await supabase.from('journal_lines').insert(jlRows);
  if (jlErr) throw jlErr;

  return je;
}

/**
 * Reverse a journal entry by posting an equal-and-opposite reversal entry.
 *
 * The original entry stays POSTED and the reversal is POSTED too, so the two
 * net to zero across every report (which sum all POSTED lines). We do NOT flip
 * the original to VOID — doing both (void original + post reversal) would remove
 * the entry twice and leave a dangling negative. The original is flagged
 * is_reversed for the audit trail.
 */
export async function voidJournal(journal_entry_id, reason, voided_by) {
  const { data: je } = await supabase.from('journal_entries').select('*, journal_lines(*)').eq('id', journal_entry_id).single();
  if (!je) return;
  if (je.is_reversed) return; // already reversed — don't double-reverse
  // Never reverse a reversal: it already cancels an earlier entry, so reversing it brings
  // that entry back to life. Every void/cancel sweep that selects "all journals for X"
  // otherwise resurrects costs/AR from an earlier edit (found on 9 void invoices).
  if (je.reversal_of) return;

  // Create reversal
  const rev = await insertEntryWithRetry('REV-', true, (entry_number) => ({
    entry_number,
    // Dated to match the original, not today — a reversal dated "today" while its
    // original sits in an earlier period makes that period's reports (P&L especially)
    // swing wildly even though all-time totals stay correct. Learned this the hard way
    // fixing a bulk correction earlier — same fix belongs here since every void/re-edit
    // flow in the app goes through this one function.
    entry_date: je.entry_date,
    description: 'REVERSAL: ' + je.description + (reason ? ' | ' + reason : ''),
    reference_type: je.reference_type,
    reference_id: je.reference_id,
    is_auto_posted: true,
    reversal_of: je.id,
    total_debit: je.total_credit,
    total_credit: je.total_debit,
    currency_id: je.currency_id,
    exchange_rate: je.exchange_rate,
    status: 'POSTED',
    created_by: voided_by,
    branch_id: je.branch_id || null,
  }));

  const revLines = (je.journal_lines || []).map((l, i) => ({
    journal_entry_id: rev.id,
    account_id: l.account_id,
    description: 'REVERSAL: ' + (l.description || ''),
    debit_amount: l.credit_amount,
    credit_amount: l.debit_amount,
    currency_id: l.currency_id,
    exchange_rate: l.exchange_rate,
    base_debit: l.base_credit,
    base_credit: l.base_debit,
    party_type: l.party_type || null,
    party_id: l.party_id || null,
    line_order: i,
    branch_id: l.branch_id || null,
  }));

  await supabase.from('journal_lines').insert(revLines);

  // Flag the original as reversed for the audit trail, but keep it POSTED so
  // original + reversal net to zero in the ledger.
  await supabase.from('journal_entries').update({ is_reversed: true, void_reason: reason, voided_by, voided_at: new Date().toISOString() }).eq('id', journal_entry_id);

  return rev;
}
